import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cartService from '../services/cartService';
import { toast } from 'react-toastify';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    // Utility function to get responsive toast position
    const getToastPosition = () => {
        return window.innerWidth >= 1024 ? 'top-center' : 'bottom-center';
    };

    // Fetch cart from backend
    const fetchCart = useCallback(async () => {
        // Only fetch if user is logged in
        if (!localStorage.getItem("user-id")) {
            setCartItems([]);
            setIsInitialized(true);
            return;
        }

        try {
            setIsLoading(true);
            const cart = await cartService.getCart();
            setCartItems(cart.items || []);
            setIsInitialized(true);
        } catch (error) {
            console.error('Error fetching cart:', error);
            setCartItems([]);
            setIsInitialized(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initialize cart on mount and when user logs in
    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    // No success/info toasts for cart mutations: the inline feedback
    // ("✓ Added" state, cart badge, sticky bar total) already confirms the
    // action, and a bottom toast would cover the sticky cart bar's CTA on
    // mobile. Toasts remain for failures only.

    // Add item to cart
    const addToCart = async (item, showToast = true) => {
        try {
            setIsLoading(true);
            const updatedCart = await cartService.addToCart(item);
            setCartItems(updatedCart.items || []);
            return updatedCart;
        } catch (error) {
            console.error('Error adding to cart:', error);
            if (showToast) {
                toast.error('Failed to add item to cart.', { position: getToastPosition(), autoClose: 4000 });
            }
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Add multiple items to cart (batch operation)
    const addMultipleToCart = async (items) => {
        try {
            setIsLoading(true);
            let updatedCart;

            for (const item of items) {
                updatedCart = await cartService.addToCart(item);
                setCartItems(updatedCart.items || []);
            }

            return updatedCart;
        } catch (error) {
            console.error('Error adding items to cart:', error);
            toast.error('Failed to add items to cart.', { position: getToastPosition(), autoClose: 4000 });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Remove item from cart by itemId
    const removeFromCart = async (itemId) => {
        try {
            setIsLoading(true);
            const updatedCart = await cartService.removeFromCart(itemId);
            setCartItems(updatedCart.items || []);
            return updatedCart;
        } catch (error) {
            console.error('Error removing from cart:', error);
            toast.error('Failed to remove item from cart.', { position: getToastPosition(), autoClose: 4000 });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Remove by week and kid (for backward compatibility)
    const removeByWeekAndKid = async (weekId, kidId) => {
        try {
            setIsLoading(true);
            const updatedCart = await cartService.removeByWeekAndKid(weekId, kidId);
            setCartItems(updatedCart.items || []);
            return updatedCart;
        } catch (error) {
            console.error('Error removing from cart:', error);
            toast.error('Failed to remove item from cart.', { position: getToastPosition(), autoClose: 4000 });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Update cart item
    const updateCartItem = async (itemId, updates) => {
        try {
            setIsLoading(true);
            const updatedCart = await cartService.updateCartItem(itemId, updates);
            setCartItems(updatedCart.items || []);
            return updatedCart;
        } catch (error) {
            console.error('Error updating cart item:', error);
            toast.error('Failed to update cart item.', { position: getToastPosition() });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Clear entire cart
    const clearCart = async () => {
        try {
            setIsLoading(true);
            await cartService.clearCart();
            setCartItems([]);
            return { items: [] };
        } catch (error) {
            console.error('Error clearing cart:', error);
            toast.error('Failed to clear cart.', { position: getToastPosition() });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Get cart item count
    const getCartCount = () => {
        return cartItems.length;
    };

    // Check if an item is in cart
    const isInCart = (weekId, kidId) => {
        return cartItems.some(item => 
            item.week._id === weekId && item.kid._id === kidId
        );
    };

    // Get specific item from cart
    const getCartItem = (weekId, kidId) => {
        return cartItems.find(item => 
            item.week._id === weekId && item.kid._id === kidId
        );
    };

    const value = {
        cartItems,
        isLoading,
        isInitialized,
        fetchCart,
        addToCart,
        addMultipleToCart,
        removeFromCart,
        removeByWeekAndKid,
        updateCartItem,
        clearCart,
        getCartCount,
        isInCart,
        getCartItem
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};